import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../features/auth/AuthProvider";
import { useWorkspace } from "../../features/workspace/WorkspaceContext";
import { Button } from "../../components/ui/Button";
import { ChangePasswordDialog } from "../../features/auth/ChangePasswordDialog";
import { resolveBranding } from "../../config/branding";

const MOBILE_QUERY = "(max-width: 768px)";
const SIDEBAR_PREF_KEY = "ml.sidebar.collapsed";

const ICONS = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  ),
  leads: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  team: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  members: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 3v18h18" />
      <path d="M7 14l3-3 4 4 5-6" />
    </svg>
  ),
  superadmin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 2l9 4-9 4-9-4 9-4z" />
      <path d="M3 11l9 4 9-4" />
      <path d="M3 16l9 4 9-4" />
    </svg>
  ),
  trash: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  collapse: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  expand: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
};

function NavItem({
  to,
  icon,
  label,
  end,
  collapsed,
  onNavigate,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={({ isActive }) => (isActive ? "nav-item active" : "nav-item")}
    >
      <span className="nav-icon">{icon}</span>
      <span className="nav-label">{label}</span>
    </NavLink>
  );
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const { current, workspaces, setSlug } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const branding = resolveBranding();

  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.matchMedia(MOBILE_QUERY).matches : false,
  );
  const [desktopCollapsed, setDesktopCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SIDEBAR_PREF_KEY) === "1";
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    document.title = branding.productName;
  }, [branding.productName]);

  // Track viewport.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      // Switching from mobile → desktop closes the drawer; the desktop
      // collapsed state continues to apply on its own.
      if (!e.matches) setMobileOpen(false);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Persist desktop preference.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SIDEBAR_PREF_KEY, desktopCollapsed ? "1" : "0");
  }, [desktopCollapsed]);

  // Auto-close mobile drawer on route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while mobile drawer is open.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (isMobile && mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isMobile, mobileOpen]);

  // Escape closes mobile drawer.
  useEffect(() => {
    if (!isMobile || !mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile, mobileOpen]);

  const toggleSidebar = useCallback(() => {
    if (isMobile) setMobileOpen((v) => !v);
    else setDesktopCollapsed((v) => !v);
  }, [isMobile]);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  if (!user) return null;

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const canSeeTrash = user.role !== "sales" || user.isSuperadmin;
  // Member performance pages are manager+ only — sales doesn't see other
  // teammates' metrics. Mirrors the server-side requireRole("manager") gate.
  const canSeeMembers =
    user.isSuperadmin || user.role === "manager" || user.role === "admin";
  // Team management is hidden from sales reps. Mirrors the TeamPage redirect
  // guard and the server-side requireRole("manager") on mutating /users routes.
  const canSeeTeam = user.isSuperadmin || user.role !== "sales";

  const collapsed = !isMobile && desktopCollapsed;

  const shellClass = [
    "app-shell",
    collapsed ? "sidebar-collapsed" : "",
    isMobile ? "is-mobile" : "",
    isMobile && mobileOpen ? "drawer-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const sidebarClass = [
    "sidebar",
    collapsed ? "collapsed" : "",
    isMobile ? "as-drawer" : "",
    isMobile && mobileOpen ? "open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={shellClass}>
      {isMobile ? (
        <div
          className={`sidebar-backdrop ${mobileOpen ? "visible" : ""}`}
          onClick={closeMobile}
          aria-hidden={!mobileOpen}
        />
      ) : null}

      <aside
        className={sidebarClass}
        aria-hidden={isMobile && !mobileOpen}
      >
        <div className="sidebar-brand">
          {branding.logo ? (
            <img src={branding.logo} alt="Logo" className="sidebar-brand-logo" />
          ) : (
            <div className="dot" />
          )}
          <div className="sidebar-brand-text">
            <div className="sidebar-brand-name">{branding.productName}</div>
            <div className="sidebar-brand-sub">{user.isSuperadmin ? "Superadmin" : user.role}</div>
          </div>
          {!isMobile ? (
            <button
              type="button"
              className="sidebar-toggle"
              onClick={toggleSidebar}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!collapsed}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? ICONS.expand : ICONS.collapse}
            </button>
          ) : (
            <button
              type="button"
              className="sidebar-toggle"
              onClick={closeMobile}
              aria-label="Close menu"
              title="Close menu"
            >
              {ICONS.close}
            </button>
          )}
        </div>

        {isMobile && user.isSuperadmin ? (
          <div className="sidebar-workspace">
            <label htmlFor="ws-picker-side" className="sidebar-workspace-label">
              Workspace
            </label>
            <select
              id="ws-picker-side"
              value={current?.slug ?? ""}
              onChange={(e) => setSlug(e.target.value)}
            >
              {workspaces.map((w) => (
                <option key={w.slug} value={w.slug}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <nav className="sidebar-nav">
          <NavItem to="/dashboard" icon={ICONS.dashboard} label="Dashboard" collapsed={collapsed} onNavigate={closeMobile} />
          <NavItem to="/leads" icon={ICONS.leads} label="Leads" end collapsed={collapsed} onNavigate={closeMobile} />
          {canSeeMembers ? (
            <NavItem to="/members" icon={ICONS.members} label="Members" collapsed={collapsed} onNavigate={closeMobile} />
          ) : null}
          {canSeeTeam ? (
            <NavItem to="/team" icon={ICONS.team} label="Team" collapsed={collapsed} onNavigate={closeMobile} />
          ) : null}
          {user.isSuperadmin ? (
            <NavItem to="/superadmin" icon={ICONS.superadmin} label="Superadmin" collapsed={collapsed} onNavigate={closeMobile} />
          ) : null}

          <div className="nav-spacer" />

          {canSeeTrash ? (
            <div className="nav-bottom">
              <NavItem to="/leads/_trash" icon={ICONS.trash} label="Trash" collapsed={collapsed} onNavigate={closeMobile} />
            </div>
          ) : null}
        </nav>

        <div className="sidebar-footer">
          <div className="user-chip" title={collapsed ? `${user.name} · ${user.email}` : undefined}>
            <div className="avatar">{user.name.slice(0, 1).toUpperCase()}</div>
            <div className="user-chip-text">
              <div className="user-name">{user.name}</div>
              <div className="user-email">{user.email}</div>
            </div>
          </div>
          {!collapsed ? (
            <button
              type="button"
              className="sidebar-change-password"
              onClick={() => setShowChangePassword(true)}
            >
              Change password
            </button>
          ) : null}
          <Button
            variant="danger"
            size="sm"
            onClick={onLogout}
            className="sidebar-signout"
            aria-label="Sign out"
            title={collapsed ? "Sign out" : undefined}
          >
            <span className="sidebar-signout-label">Sign out</span>
            <span className="sidebar-signout-icon" aria-hidden>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </span>
          </Button>
        </div>
      </aside>

      <div className="main-pane">
        <header className="topbar">
          {isMobile ? (
            <>
              <button
                type="button"
                className="topbar-menu-btn"
                onClick={toggleSidebar}
                aria-label="Open menu"
                aria-expanded={mobileOpen}
              >
                {ICONS.menu}
              </button>
              <div className="topbar-brand">
                {branding.logo ? (
                  <img src={branding.logo} alt="" className="topbar-brand-logo" />
                ) : (
                  <div className="dot" />
                )}
                <div className="topbar-brand-name">{branding.productName}</div>
              </div>
            </>
          ) : user.isSuperadmin ? (
            <div className="workspace-switcher">
              <label htmlFor="ws-picker" className="topbar-label">Workspace</label>
              <select
                id="ws-picker"
                value={current?.slug ?? ""}
                onChange={(e) => setSlug(e.target.value)}
              >
                {workspaces.map((w) => (
                  <option key={w.slug} value={w.slug}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="workspace-name">Lead Management CRM</div>
          )}
        </header>
        <div className="page-pane">
          <Outlet />
        </div>
      </div>

      {showChangePassword ? (
        <ChangePasswordDialog onClose={() => setShowChangePassword(false)} />
      ) : null}
    </div>
  );
}
